import { InformationRequestStatus } from "@tfg/db"
import { createContext, ReactNode, useEffect } from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"
import { updateOperationsValidationType } from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.request.dto"

import { CustomFieldValueWithDefinitionAndDocumentResponseDto } from "~/gen/api"
import { OperationNotCreated } from "~/pages/SupplierPortal/TraceabilityRequests/components/OperationNotCreated"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"
import { getOperationItemIsTransferred } from "~/pages/SupplierPortal/TraceabilityRequests/utils/traceability.utils"

import { useUpdateOperationsMutation } from "../hooks"
import {
  OperationItem,
  allChildrenItems,
  nodeRequiresRef,
  calculateOperationProgress,
  RequestWithOpItemFromOperationTree,
} from "../utils"

export type TraceabilityOperationsContextType = {
  request: RequestWithOpItemFromOperationTree
  status: InformationRequestStatus
  operationItems: OperationItem[]
  updateOperationAndChildren: (
    update: updateOperationsValidationType["update"]
  ) => void
  isTranfering: boolean
  isReinforcedTraceability: boolean
  operationItem: OperationItem
  isTransferred: boolean
  currentContactId: string
  user: LibraryResponses.TFGUser
  isAdmin: boolean
  mandatoryReference: boolean
}

export const TraceabilityOperationsContext =
  createContext<TraceabilityOperationsContextType | null>(null)

export function TraceabilityOperationsProvider({
  children,
  request,
  status,
  operationItems,
  operationItem,
  setProgress,
}: {
  children: ReactNode
  request: RequestWithOpItemFromOperationTree
  status: InformationRequestStatus
  operationItems: OperationItem[]
  operationItem: OperationItem
  setProgress: (operationId: number, value: number) => void
}) {
  const { user, isAdmin } = useInformationRequestContext()
  const isReinforcedTraceability =
    request.original.reinforcedTraceability && operationItem.rank >= 2
  const currentContactId = request.original.contactId
  const isTransferred = getOperationItemIsTransferred(
    operationItem,
    operationItem.operation?.owner.id,
    isReinforcedTraceability
  )
  const { customFieldValues } = useInformationRequestContext()

  useEffect(() => {
    setProgress(
      operationItem.operation?.id ?? 0,
      calculateOperationProgress(
        isTransferred,
        operationItem,
        operationItems,
        customFieldValues as unknown as CustomFieldValueWithDefinitionAndDocumentResponseDto[]
      )
    )
  }, [
    operationItem,
    customFieldValues,
    isTransferred,
    operationItems,
    setProgress,
  ])

  const mandatoryReference = nodeRequiresRef(
    operationItem.parentOperation ?? null,
    operationItem.operation ?? null
  )

  const { mutate: updateOperationsMutation, isPending: isTranfering } =
    useUpdateOperationsMutation(request.original.id)

  const updateOperationAndChildren = (
    update: updateOperationsValidationType["update"]
  ) => {
    if (shouldSkipUpdate(update.contactId, currentContactId)) {
      return
    }

    const eligibleOperations = getEligibleOperations(
      operationItem,
      operationItems
    )
    const operationIds = filterOperationIds(
      eligibleOperations,
      update,
      isReinforcedTraceability
    )

    if (operationIds.length > 0) {
      updateOperationsMutation({ ids: operationIds, update })
    }
  }

  if (!user?.id) {
    return null
  }

  if (!operationItem.operation) {
    return <OperationNotCreated operationItem={operationItem} />
  }

  return (
    <TraceabilityOperationsContext.Provider
      value={{
        request,
        status,
        operationItems,
        updateOperationAndChildren,
        isTranfering,
        isReinforcedTraceability,
        operationItem,
        isTransferred,
        currentContactId,
        user,
        isAdmin,
        mandatoryReference,
      }}
    >
      {children}
    </TraceabilityOperationsContext.Provider>
  )
}

const shouldSkipUpdate = (newContactId?: string, currentContactId?: string) => {
  return newContactId === currentContactId
}

const getEligibleOperations = (
  currentOperation: OperationItem,
  allOperations: OperationItem[]
): OperationItem[] => {
  return [
    currentOperation,
    ...allChildrenItems(allOperations, currentOperation.nodeId),
  ]
}

const filterOperationIds = (
  operations: OperationItem[],
  update: updateOperationsValidationType["update"],
  isReinforcedTraceability: boolean
): number[] => {
  if (operations.length === 0) {
    return []
  }
  return operations.flatMap((operation: OperationItem): number[] => {
    if (
      !operation.operation ||
      operation.operation.productionSiteId ||
      !isValidOperation(operation)
    ) {
      return []
    }

    if (isReinforcedTraceability) {
      const id = handleReinforcedTraceability(operation, update)
      if (id) {
        return [id]
      }
    }

    return [operation.operation.id]
  })
}

const isValidOperation = (operation: OperationItem): boolean => {
  return !!(operation.operation && !operation.operation.productionSiteId)
}

const handleReinforcedTraceability = (
  operation: OperationItem,
  update: updateOperationsValidationType["update"]
): number | null => {
  if (!operation.operation || update.doNotKnow !== undefined) {
    return null
  }
  return operation.operation.id
}
